import { CommonModule } from '@angular/common';
import {
  OnDestroy,
  OnInit} from '@angular/core';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { RdsModule } from '@rabobank/rds-angular';
import { FormsModule } from '@angular/forms';
import { ConnectorConfigService } from '../../services/connector-config.service';
import { ConnectorOption } from '../../models/batch.interface';
import { BatchService } from '../../services/batch.service';
import { BatchStateService } from '../../services/batch-state.service';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AuthorizationService } from '../../services/authorization.service';
import { Subject, filter, takeUntil } from 'rxjs';
import { InteractionStatus, EventType, EventMessage, AccountInfo } from '@azure/msal-browser';

@Component({
  selector: 'app-profile-selector',
  imports: [CommonModule, RdsModule, FormsModule],
  templateUrl: './profile-selector.component.html',
  styleUrl: './profile-selector.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProfileSelectorComponent implements OnInit, OnDestroy {
  public selectedConnector: string = '';
  public connectorOptions: ConnectorOption[] = [];

  // NEW: location state and catalog
  public selectedLocation: string = '';
  public locationsByConnector: Record<
    string,
    { value: string; viewValue: string }[]
  > = {
    // You can expand this later (e.g., PFNA → North America)
    PFEU: [{ value: 'EU', viewValue: 'Europe' }],
  };

  private readonly _destroy = new Subject<void>();

  constructor(
    private configService: ConnectorConfigService,
    private batchService: BatchService,
    private batchStateService: BatchStateService,
    private router: Router,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private authorizationService: AuthorizationService
  ) {}

  public ngOnInit(): void {
    // Initially compute options (may be empty if groups not yet available)
    this.computeOptions();

    // Recompute when MSAL reports interaction finished so group claims are available
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroy)
      )
      .subscribe(() => {
        this.computeOptions();
      });

    // Recompute when login/token events arrive during first-load auth redirects.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this._destroy)
      )
      .subscribe(() => {
        this.computeOptions();
      });
  }

  public ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  /** Helper getters for location dropdown and Start button enablement */
  public get locationsForSelected(): { value: string; viewValue: string }[] {
    return this.locationsByConnector[this.selectedConnector] ?? [];
  }

  public get showLocationSelect(): boolean {
    return this.locationsForSelected.length > 0;
  }

  public get canStart(): boolean {
    if (!this.selectedConnector) return false;
    if (this.showLocationSelect && !this.selectedLocation) return false;
    return true;
  }

  /** Reset/auto-select location when connector changes */
  public onConnectorChange(connector: string): void {
    this.selectedConnector = connector;

    const locs = this.locationsForSelected;
    // Auto-select the only location available to streamline UX
    this.selectedLocation = locs.length === 1 ? locs[0].value : '';
  }

  private async computeOptions(): Promise<void> {
    const allOptions = this.configService.getConnectorOptions();
    const account = this.resolveActiveAccount();

    if (!account) {
      this.connectorOptions = [];
      return;
    }

    try {
      this.connectorOptions = await this.authorizationService.getAvailableConnectors(
        account,
        allOptions
      );
      if (this.connectorOptions.length > 0) {
        this.onConnectorChange(this.connectorOptions[0].value);
      } else {
        this.selectedConnector = '';
        this.selectedLocation = '';
      }
    } catch (e) {
      console.warn('[ProfileSelector] failed to compute options', e);
      this.connectorOptions = [];
    }
  }

  private resolveActiveAccount(): AccountInfo | null {
    const active = this.msalService.instance.getActiveAccount();
    if (active) return active;

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      return accounts[0];
    }

    return null;
  }

  public async start(): Promise<void> {
    if (!this.selectedConnector) return;

    const account = this.msalService.instance.getActiveAccount();
    if (!account) return;

    try {
      const auth = await this.authorizationService.evaluateAuthorization(account);
      if (!auth.isAdmin) {
        const allowed = await this.authorizationService.getAvailableConnectors(
          account,
          [{ value: this.selectedConnector, viewValue: this.selectedConnector }]
        );
        if (allowed.length === 0) {
          console.warn(
            'User is not allowed to access connector',
            this.selectedConnector
          );
          return;
        }
      }
    } catch (error) {
      console.warn('Failed to validate connector access during start', error);
      if (!account) {
        console.warn(
          'User is not allowed to access connector',
          this.selectedConnector
        );
        return;
      }
    }

    // If connector requires a location, enforce it
    if (this.showLocationSelect && !this.selectedLocation) {
      return;
    }

    // Notify other parts of the app
    // Clear any persisted checkbox selections so switching applications does not keep previous selections
    try {
      const current = this.batchStateService.getState();
      this.batchStateService.saveState({
        ...current,
        checkedBatches: [],
        selectedBatch: undefined,
      });
    } catch (_e) {
      /* ignore */
    }

    this.batchService.selectedConnector.next(this.selectedConnector);
    // OPTIONAL: share location too, if BatchService supports it
    // this.batchService.selectedLocation?.next(this.selectedLocation);

    // Navigate
    const path = this.getRouteForConnector(this.selectedConnector);
    if (path) {
      // OPTIONAL: pass location along as a query param (useful if multiple locations exist later)
      // this.router.navigate([path], { queryParams: { location: this.selectedLocation } });

      this.router.navigate([path]);
    } else {
      this.router.navigate(['/batches']);
    }
  }

  private getRouteForConnector(connector: string): string | null {
    switch (connector) {
      case 'HK_TRADE':
        return '/batches/hk-trade';
      case 'AG_DESK':
        return '/batches/agency-desk';
      case 'GNM':
        return '/batches/gnm';
      case 'FEES_MGMT':
        return '/batches/fees';
      case 'HK_LC':
        return '/batches/hk-lc';
      case 'PFEU':
        return '/batches/pf-eu';
      case 'PFNA':
        return '/batches/pf-na';
      case 'VCF':
        return '/batches/vcf';
      default:
        return null;
    }
  }
}
